import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Image,
    Platform,
    ScrollView,
    Text,
    TextInput as RNTextInput,
    TouchableOpacity,
    View,
} from "react-native";
import { TextInput } from "react-native-paper";
import {useTranslation} from "react-i18next";
import { LinearGradient } from "expo-linear-gradient";
import { Ionicons } from "@expo/vector-icons";
import axios from "axios";
import { useNavigation } from "@react-navigation/native";
import i18n from "../../i18n";
import { ApiConfig } from "../config/ApiConfig";
import ToastAlert from "../helpers/ToastAlert";
import { identifierValidator, isEmailIdentifier } from "../helpers/identifierValidator";
import { evaluatePassword, PASSWORD_RULES } from "../helpers/passwordStrength";
import { useTheme } from "../helpers/ThemeContext";
import { useThemedStyles } from "../helpers/useThemedStyles";
import { BRAND, withAlpha } from "../helpers/managementTheme";

const LogoImage = require("../../assets/logo-small.png");
const OTP_BASE = `${ApiConfig.apiURL}/api/auth/password/otp`;
const RESEND_COOLDOWN = 45;

const getRuleLabels = () => ({
    length: i18n.t("pwd_rule_length"),
    lowercase: i18n.t("pwd_rule_lowercase"),
    uppercase: i18n.t("pwd_rule_uppercase"),
    number: i18n.t("pwd_rule_number"),
    special: i18n.t("pwd_rule_special"),
});

const STRENGTH_COLORS = ["#ef4444", "#ef4444", "#f59e0b", "#22c55e", "#16a34a"];
const getStrengthLabels = () => [
    i18n.t("pwd_strength_very_weak"),
    i18n.t("pwd_strength_weak"),
    i18n.t("pwd_strength_fair"),
    i18n.t("pwd_strength_good"),
    i18n.t("pwd_strength_strong"),
];

const OtpBoxes = ({ value, onChange, disabled, styles }) => {
    const refs = useRef([]);
    const [focused, setFocused] = useState(-1);
    const digits = String(value).padEnd(6, " ").slice(0, 6).split("");

    const handleChange = (index, raw) => {
        const clean = raw.replace(/\D/g, "");

        if (clean.length > 1) {
            onChange(clean.slice(0, 6));
            refs.current[Math.min(clean.length, 5)]?.focus();

            return;
        }
        const next = digits.map((d) => (d === " " ? "" : d));

        next[index] = clean;
        onChange(next.join("").replace(/\s/g, ""));
        if (clean && index < 5) refs.current[index + 1]?.focus();
    };

    const handleKey = (index, e) => {
        if (e.nativeEvent.key === "Backspace" && !digits[index].trim() && index > 0) {
            refs.current[index - 1]?.focus();
        }
    };

    const renderBox = (i) => (
        <RNTextInput
            key={i}
            ref={(el) => (refs.current[i] = el)}
            style={[styles.otpBox, focused === i && styles.otpBoxFocused, !!digits[i].trim() && styles.otpBoxFilled]}
            onFocus={() => setFocused(i)}
            onBlur={() => setFocused((current) => (current === i ? -1 : current))}
            keyboardType="number-pad"
            maxLength={6}
            editable={!disabled}
            value={digits[i].trim()}
            onChangeText={(text) => handleChange(i, text)}
            onKeyPress={(e) => handleKey(i, e)}
            textContentType="oneTimeCode"
            autoComplete="sms-otp"
        />
    );

    return (
        <View style={styles.otpRow}>
            {[0, 1, 2].map(renderBox)}
            <Text style={styles.otpDash}>–</Text>
            {[3, 4, 5].map(renderBox)}
        </View>
    );
};

const ForgotPasswordScreen = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const styles = useThemedStyles(buildStyles);
    const navigation = useNavigation();
    const [step, setStep] = useState("identify"); // identify | verify | reset | done
    const [identifier, setIdentifier] = useState("");
    const [channel, setChannel] = useState("email");
    const [hint, setHint] = useState("");
    // True when the backend degraded an SMS-OTP send to e-mail because the SMS
    // provider is out of credit (channel becomes 'email', hint the masked address).
    const [smsDegraded, setSmsDegraded] = useState(false);
    const [otp, setOtp] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [cooldown, setCooldown] = useState(0);

    const strength = useMemo(() => evaluatePassword(password), [password]);
    // Premium outlined fields: rounded outline that turns brand orange on focus.
    const inputProps = {
        mode: "outlined",
        outlineColor: theme.colors.border,
        activeOutlineColor: BRAND.base,
        outlineStyle: styles.inputOutline,
    };

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);

        return () => clearInterval(id);
    }, [cooldown]);

    const requestCode = async () => {
        setError("");
        const value = identifier.trim();
        const validation = identifierValidator(value);

        if (validation) {
            setError(validation);

            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${OTP_BASE}/request`, { identifier: value });
            const degraded = !!data?.smsDegraded;

            setChannel(data?.channel || (isEmailIdentifier(value) ? "email" : "sms"));
            setHint(data?.hint || value);
            setSmsDegraded(degraded);
            setOtp("");
            setStep("verify");
            setCooldown(RESEND_COOLDOWN);
            if (degraded) {
                ToastAlert("info", "top", t("info"), t("forgot_pw_sms_fallback_email", { hint: data?.hint || "" }), 5000);
            }
        } catch (err) {
            const status = err?.response?.status;

            if (status === 429) setError(t("forgot_pw_too_many_attempts"));
            // 502 = delivery failure; the backend now reports e-mail failures too
            // (it used to answer ok and the code never arrived).
            else if (status === 502) setError(isEmailIdentifier(value) ? t("forgot_pw_email_failed") : t("forgot_pw_sms_failed"));
            else setError(t("forgot_pw_code_send_failed"));
        } finally {
            setLoading(false);
        }
    };

    const verifyCode = async () => {
        setError("");
        if (otp.replace(/\D/g, "").length !== 6) {
            setError(t("forgot_pw_enter_full_code"));

            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${OTP_BASE}/verify`, {
                identifier: identifier.trim(),
                code: otp.replace(/\D/g, ""),
            });

            setResetToken(data?.resetToken || "");
            setStep("reset");
        } catch (err) {
            const status = err?.response?.status;

            if (status === 429) setError(t("forgot_pw_too_many_failed"));
            else setError(t("forgot_pw_code_invalid_expired"));
        } finally {
            setLoading(false);
        }
    };

    const submitPassword = async () => {
        setError("");
        if (!strength.valid) {
            setError(t("forgot_pw_does_not_meet_requirements"));

            return;
        }
        if (password !== confirm) {
            setError(t("forgot_pw_passwords_mismatch"));

            return;
        }
        setLoading(true);
        try {
            await axios.post(`${OTP_BASE}/reset`, { resetToken, password });
            setStep("done");
        } catch (err) {
            const status = err?.response?.status;

            // 422 = the new password is in a known breach (HIBP) — show the reason.
            if (status === 422) setError(err?.response?.data?.message || t("forgot_pw_does_not_meet_requirements"));
            else if (status === 400) setError(t("forgot_pw_session_expired"));
            else setError(t("forgot_pw_change_failed"));
        } finally {
            setLoading(false);
        }
    };

    const goToLogin = () => {
        if (step === "done") {
            ToastAlert("success", "top", t("forgot_pw_change_success_title"), t("forgot_pw_change_success_body"), 4000);
        }
        navigation.navigate("LoginScreen");
    };

    return (
        <LinearGradient colors={BRAND.gradient} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.background}>
            <View pointerEvents="none" style={styles.blobA} />
            <View pointerEvents="none" style={styles.blobB} />
            <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
                <View style={styles.container}>
                    {Platform.OS !== "web" && (
                        <View style={styles.logoPlate}>
                            <Image source={LogoImage} style={styles.logo} resizeMode="contain" />
                        </View>
                    )}

                    <View style={styles.steps}>
                        {["identify", "verify", "reset"].map((s, i) => {
                            const order = ["identify", "verify", "reset", "done"];
                            const active = step === s;
                            const done = order.indexOf(step) > i;

                            return (
                                <View
                                    key={s}
                                    style={[styles.stepDot, done && styles.stepDotDone, active && styles.stepDotActive]}
                                />
                            );
                        })}
                    </View>

                    {step === "identify" && (
                        <View style={styles.card}>
                            <View style={styles.iconCircle}>
                                <Ionicons name="key-outline" size={26} color={BRAND.base} />
                            </View>
                            <Text style={styles.title}>{t('forgot_pw_title')}</Text>
                            <Text style={styles.subtitle}>
                                {t('forgot_pw_identify_subtitle')}
                            </Text>
                            <TextInput
                                {...inputProps}
                                label={t('forgot_pw_identifier_label')}
                                value={identifier}
                                onChangeText={(text) => { setIdentifier(text); setError(""); }}
                                autoCapitalize="none"
                                keyboardType="email-address"
                                style={styles.input}
                                left={<TextInput.Icon icon={isEmailIdentifier(identifier) ? "email-outline" : "cellphone"} />}
                            />
                            {!!error && <Text style={styles.error}>{error}</Text>}
                            <TouchableOpacity style={styles.primaryButton} onPress={requestCode} disabled={loading}>
                                <Text style={styles.primaryText}>{loading ? t("forgot_pw_sending") : t("forgot_pw_send_code")}</Text>
                            </TouchableOpacity>
                            <TouchableOpacity onPress={goToLogin}>
                                <Text style={styles.link}>{t('forgot_pw_back_to_login')}</Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {step === "verify" && (
                        <View style={styles.card}>
                            <View style={styles.iconCircle}>
                                <Ionicons
                                    name={channel === "whatsapp" ? "logo-whatsapp" : channel === "sms" ? "chatbubble-ellipses-outline" : "mail-open-outline"}
                                    size={26}
                                    color={channel === "whatsapp" ? "#25D366" : BRAND.base}
                                />
                            </View>
                            <Text style={styles.title}>{t('forgot_pw_enter_code_title')}</Text>
                            <Text style={styles.subtitle}>
                                {channel === "whatsapp"
                                    ? t("forgot_pw_code_sent_whatsapp")
                                    : channel === "sms"
                                        ? t("forgot_pw_code_sent_sms")
                                        : t("forgot_pw_code_sent_email")}
                                <Text style={styles.bold}>{hint}</Text>
                            </Text>
                            {smsDegraded && (
                                <View style={styles.warningRow}>
                                    <Ionicons name="alert-circle-outline" size={16} color="#b45309" />
                                    <Text style={styles.warning}>
                                        {t("forgot_pw_sms_fallback_email", { hint })}
                                    </Text>
                                </View>
                            )}
                            <OtpBoxes value={otp} onChange={(v) => { setOtp(v); setError(""); }} disabled={loading} styles={styles} />
                            {!!error && <Text style={styles.error}>{error}</Text>}
                            <TouchableOpacity style={styles.primaryButton} onPress={verifyCode} disabled={loading}>
                                <Text style={styles.primaryText}>{loading ? t("forgot_pw_verifying") : t("forgot_pw_verify_code")}</Text>
                            </TouchableOpacity>
                            <View style={styles.resendRow}>
                                {cooldown > 0 ? (
                                    <Text style={styles.muted}>{t('forgot_pw_resend_in', { count: cooldown })}</Text>
                                ) : (
                                    <TouchableOpacity onPress={requestCode} disabled={loading}>
                                        <Text style={styles.link}>{t('forgot_pw_resend_code')}</Text>
                                    </TouchableOpacity>
                                )}
                            </View>
                            <TouchableOpacity onPress={() => { setStep("identify"); setError(""); setSmsDegraded(false); }}>
                                <Text style={styles.link}>{t('forgot_pw_other_identifier')}</Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {step === "reset" && (
                        <View style={styles.card}>
                            <View style={styles.iconCircle}>
                                <Ionicons name="lock-closed-outline" size={26} color={BRAND.base} />
                            </View>
                            <Text style={styles.title}>{t('forgot_pw_new_password_title')}</Text>
                            <Text style={styles.subtitle}>{t('forgot_pw_new_password_subtitle')}</Text>

                            <TextInput
                                {...inputProps}
                                label={t('forgot_pw_new_password_label')}
                                value={password}
                                onChangeText={(text) => { setPassword(text); setError(""); }}
                                secureTextEntry={!showPassword}
                                autoCapitalize="none"
                                style={styles.input}
                                right={
                                    <TextInput.Icon
                                        icon={showPassword ? "eye-off" : "eye"}
                                        onPress={() => setShowPassword((v) => !v)}
                                    />
                                }
                            />

                            <View style={styles.strengthRow}>
                                <View style={styles.strengthTrack}>
                                    <View
                                        style={[
                                            styles.strengthFill,
                                            { width: `${strength.percent}%`, backgroundColor: STRENGTH_COLORS[strength.score] },
                                        ]}
                                    />
                                </View>
                                <Text style={[styles.strengthLabel, { color: STRENGTH_COLORS[strength.score] }]}>
                                    {getStrengthLabels()[strength.score]}
                                </Text>
                            </View>

                            <View style={styles.rules}>
                                {PASSWORD_RULES.map((rule) => (
                                    <View key={rule} style={styles.ruleRow}>
                                        <Ionicons
                                            name={strength.checks[rule] ? "checkmark-circle" : "ellipse-outline"}
                                            size={16}
                                            color={strength.checks[rule] ? "#16a34a" : "#cbd5e1"}
                                        />
                                        <Text style={[styles.ruleText, strength.checks[rule] && styles.ruleTextOk]}>
                                            {getRuleLabels()[rule]}
                                        </Text>
                                    </View>
                                ))}
                            </View>

                            <TextInput
                                {...inputProps}
                                label={t('forgot_pw_confirm_password_label')}
                                value={confirm}
                                onChangeText={(text) => { setConfirm(text); setError(""); }}
                                secureTextEntry={!showPassword}
                                autoCapitalize="none"
                                style={styles.input}
                            />
                            {!!confirm && password !== confirm && (
                                <Text style={styles.error}>{t('forgot_pw_passwords_mismatch')}</Text>
                            )}
                            {!!error && <Text style={styles.error}>{error}</Text>}
                            <TouchableOpacity
                                style={[styles.primaryButton, (!strength.valid || password !== confirm) && styles.primaryButtonDisabled]}
                                onPress={submitPassword}
                                disabled={loading || !strength.valid || password !== confirm}
                            >
                                <Text style={styles.primaryText}>{loading ? t("forgot_pw_saving") : t("forgot_pw_set_new_password")}</Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {step === "done" && (
                        <View style={styles.card}>
                            <View style={[styles.iconCircle, styles.iconCircleSuccess]}>
                                <Ionicons name="checkmark" size={30} color="#16a34a" />
                            </View>
                            <Text style={styles.title}>{t('forgot_pw_done_title')}</Text>
                            <Text style={styles.subtitle}>
                                {t('forgot_pw_done_subtitle')}
                            </Text>
                            <TouchableOpacity style={styles.primaryButton} onPress={goToLogin}>
                                <Text style={styles.primaryText}>{t('forgot_pw_back_to_login')}</Text>
                            </TouchableOpacity>
                        </View>
                    )}
                </View>
            </ScrollView>
        </LinearGradient>
    );
};

const buildStyles = (theme, isDark) => ({
    background: { flex: 1, overflow: "hidden" },
    // Soft translucent discs echo the hero card of the management screens.
    blobA: { position: "absolute", width: 320, height: 320, borderRadius: 160, backgroundColor: "rgba(255,255,255,0.12)", top: -120, right: -90 },
    blobB: { position: "absolute", width: 240, height: 240, borderRadius: 120, backgroundColor: "rgba(255,255,255,0.09)", bottom: -90, left: -70 },
    scroll: { flexGrow: 1, justifyContent: "center" },
    container: { padding: 20, alignItems: "center", width: "100%" },
    // White plate keeps the logo legible on the orange hero.
    logoPlate: {
        width: 84,
        height: 84,
        borderRadius: 28,
        backgroundColor: "#FFFFFF",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18,
        shadowColor: BRAND.deep,
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.28,
        shadowRadius: 18,
        elevation: 8,
    },
    logo: { width: 58, height: 58 },
    steps: { flexDirection: "row", gap: 8, marginBottom: 18 },
    stepDot: { width: 26, height: 5, borderRadius: 99, backgroundColor: "rgba(255,255,255,0.35)" },
    stepDotDone: { backgroundColor: "rgba(255,255,255,0.75)" },
    stepDotActive: { backgroundColor: "#FFFFFF" },
    card: {
        width: "100%",
        maxWidth: 420,
        backgroundColor: isDark ? "#131B2F" : "#FFFFFF",
        borderRadius: 28,
        borderWidth: 1,
        borderColor: isDark ? "rgba(148,163,184,0.14)" : "rgba(255,255,255,0.9)",
        padding: 24,
        alignItems: "center",
        shadowColor: "#7C2D12",
        shadowOffset: { width: 0, height: 16 },
        shadowOpacity: 0.22,
        shadowRadius: 28,
        elevation: 10,
    },
    iconCircle: {
        width: 58,
        height: 58,
        borderRadius: 20,
        backgroundColor: withAlpha(BRAND.base, isDark ? 0.2 : 0.12),
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 10,
    },
    iconCircleSuccess: { backgroundColor: "rgba(22,163,74,0.14)" },
    title: { fontSize: 23, fontWeight: "800", letterSpacing: -0.4, color: theme.colors.text, marginBottom: 6, textAlign: "center" },
    subtitle: { fontSize: 14, color: theme.colors.subText, textAlign: "center", marginBottom: 18, lineHeight: 20 },
    bold: { fontWeight: "700", color: theme.colors.text },
    input: { width: "100%", marginBottom: 12, backgroundColor: isDark ? "#131B2F" : "#FFFFFF" },
    inputOutline: { borderRadius: 16, borderWidth: 1.5 },
    primaryButton: {
        backgroundColor: BRAND.base,
        height: 52,
        width: "100%",
        borderRadius: 999,
        marginTop: 6,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: BRAND.dark,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.3,
        shadowRadius: 14,
        elevation: 6,
    },
    primaryButtonDisabled: { opacity: 0.55 },
    primaryText: { fontSize: 16, color: "#FFFFFF", fontWeight: "800" },
    link: { color: BRAND.dark, fontWeight: "700", textAlign: "center", marginTop: 14, fontSize: 14 },
    muted: { color: theme.colors.subText, fontSize: 14 },
    error: { color: "#b91c1c", fontSize: 13, marginBottom: 10, textAlign: "center", width: "100%" },
    warningRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 6,
        marginTop: 4,
        marginBottom: 12,
        width: "100%",
        paddingHorizontal: 4,
    },
    warning: { color: "#b45309", fontSize: 12.5, lineHeight: 17, flex: 1 },
    resendRow: { marginTop: 14, alignItems: "center" },
    otpRow: { flexDirection: "row", alignItems: "center", justifyContent: "center", marginVertical: 8 },
    otpBox: {
        width: 42,
        height: 54,
        marginHorizontal: 4,
        borderWidth: 1.5,
        borderColor: theme.colors.border,
        borderRadius: 16,
        textAlign: "center",
        fontSize: 22,
        fontWeight: "700",
        color: theme.colors.text,
        backgroundColor: theme.colors.inputBackground,
    },
    otpBoxFilled: { borderColor: withAlpha(BRAND.base, 0.5) },
    otpBoxFocused: { borderColor: BRAND.base, backgroundColor: withAlpha(BRAND.base, isDark ? 0.14 : 0.06) },
    otpDash: { fontSize: 22, color: theme.colors.subText, marginHorizontal: 2, fontWeight: "700" },
    strengthRow: { flexDirection: "row", alignItems: "center", width: "100%", marginBottom: 12 },
    strengthTrack: { flex: 1, height: 7, borderRadius: 99, backgroundColor: theme.colors.border, overflow: "hidden", marginRight: 10 },
    strengthFill: { height: "100%", borderRadius: 99 },
    strengthLabel: { fontSize: 12, fontWeight: "700", minWidth: 80, textAlign: "right" },
    rules: { width: "100%", marginBottom: 14 },
    ruleRow: { flexDirection: "row", alignItems: "center", paddingVertical: 3 },
    ruleText: { marginLeft: 8, fontSize: 13, color: theme.colors.subText },
    ruleTextOk: { color: "#16a34a" },
});

export default ForgotPasswordScreen;

